import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable, switchMap } from 'rxjs';
import { IItemBase } from '../../../pages/items/items.interface';
import { ELoadStatus } from '../../../state/state.interface';
import { IBaseResponse, IGetManyResponse } from '../../../utilities/http-utilities.service';

export interface IInlineUserChipComponentState {
  boardMembersLoading: ELoadStatus;
  boardMembers: IBoardMembers[];
  editItemLoading: ELoadStatus;
}

export interface IBoardMembersRequest {
  boardId: number;
  search?: string;
  selectedUserId?: number | null;
}

export interface IEditItem {
  assigneeId: number;
  id: number;
}

export interface IBoardMembers {
  id: number;
  name: string;
}

@Injectable()
export class InlineUserChipStore extends ComponentStore<IInlineUserChipComponentState> {
  public readonly boardMembers: Signal<IBoardMembers[]> = this.selectSignal(
    (state: IInlineUserChipComponentState) => state.boardMembers,
  );
  public readonly boardMembersLoading: Signal<ELoadStatus> = this.selectSignal(
    (state: IInlineUserChipComponentState) => state.boardMembersLoading,
  );
  public readonly editItemLoading$: Observable<ELoadStatus> = this.select(
    (state: IInlineUserChipComponentState) => state.editItemLoading,
  );

  readonly getBoardMembersData = this.effect((trigger$: Observable<IBoardMembersRequest>) =>
    trigger$.pipe(
      switchMap((params) => {
        this.patchState({ boardMembers: [], boardMembersLoading: ELoadStatus.loading });
        let httpParams: HttpParams = new HttpParams();

        if (params.search) {
          httpParams = httpParams.append('search', params.search);
        }

        if (params.selectedUserId) {
          httpParams = httpParams.append('selectedUser', params.selectedUserId.toString());
        }

        return this.getBoardMembers(params.boardId, httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IBoardMembers>): void => {
              this.patchState({
                boardMembers: response.data,
                boardMembersLoading: ELoadStatus.success,
              });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly editItemAssigneeId = this.effect((trigger$: Observable<IEditItem>) =>
    trigger$.pipe(
      switchMap((editItem: IEditItem) => {
        this.patchState({ editItemLoading: ELoadStatus.loading });
        const params = new HttpParams().set('preventIsQuickColumnUpdate', true);

        return this.editItem(editItem, params).pipe(
          tapResponse(
            () =>
              this.patchState({
                editItemLoading: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  constructor(private readonly http: HttpClient) {
    super({
      boardMembers: [],
      boardMembersLoading: ELoadStatus.initial,
      editItemLoading: ELoadStatus.initial,
    });
  }

  private getBoardMembers(boardId: number, params: HttpParams): Observable<IGetManyResponse<IBoardMembers>> {
    return this.http.get<IGetManyResponse<IBoardMembers>>(`boards/${boardId}/members`, { params });
  }

  private editItem(item: IEditItem, params: HttpParams): Observable<IBaseResponse<IItemBase>> {
    return this.http.patch<IBaseResponse<IItemBase>>(
      `items/${item.id}`,
      {
        assigneeId: item.assigneeId,
      },
      { params },
    );
  }
}
